import { useEffect, useRef, useState, type MouseEvent } from 'react'
import { loadJourneyContent, type JourneyContent } from '../../../entities/journey'
import { getResult, nextQuestion, questions, quizSubmission, type Answer } from './breakupQuiz'
import type { OnboardingScenarioProps } from './types'
import { useBreakUpIntroduction } from './useBreakUpIntroduction'
import { useOnboardingAnalytics } from './useOnboardingAnalytics'
import { analytics } from '../../../lib/analytics'

// A stored draft may predate this quiz version; keep only a valid question path.
export function restoreQuizAnswers(value: unknown): Answer[] {
  const valid: Answer[] = []
  if (!Array.isArray(value)) return valid
  for (const answer of value) {
    const expected = nextQuestion(valid)
    if (!expected || answer?.question !== expected || !Number.isInteger(answer.choice) || answer.choice < 0 || answer.choice >= questions[expected].options.length) break
    valid.push({ question: expected, choice: answer.choice })
  }
  return valid
}

export function useBreakUpQuiz({ payload, onChange: change, onComplete, submitting }: OnboardingScenarioProps) {
  const identity = useBreakUpIntroduction({ payload, onChange: change })
  const { name, communicationStyle } = identity
  const introduced = Boolean(name.trim())
  const introduction = !payload.step || !introduced
  const answers = restoreQuizAnswers(payload.custom?.quiz_answers)
  const questionId = nextQuestion(answers)
  const [content, setContent] = useState<JourneyContent | null>(null)
  useEffect(() => {
    let active = true
    loadJourneyContent().then((loaded) => { if (active) setContent(loaded) }).catch(() => { /* surfaced on submit */ })
    return () => { active = false }
  }, [])
  const result = questionId || !content ? null : getResult(answers, content)
  useOnboardingAnalytics('quiz', introduction ? 1 : result ? 3 : questionId ? 2 : null)
  const quizTracking = useRef({ started: answers.length > 0, completed: questionId === null, answered: false })
  useEffect(() => {
    if (result && quizTracking.current.answered && !quizTracking.current.completed) {
      quizTracking.current.completed = true
      analytics.track('quizCompleted')
    }
  }, [result])
  const screenRef = useRef<HTMLElement>(null)
  const screenKey = introduction ? 'introduction' : questionId ?? 'result'
  useEffect(() => { if (!introduction) screenRef.current?.focus() }, [screenKey, introduction])

  const start = () => { if (introduced && !submitting) change({ ...payload, step: 1 }) }
  const saveAnswers = (next: Answer[]) => change({ ...payload, step: 1, custom: { ...payload.custom, variant: 'quiz', quiz_answers: next } })
  const choose = (event: MouseEvent<HTMLButtonElement>) => {
    if (!questionId || submitting) return
    const choice = Number(event.currentTarget.value)
    if (!Number.isInteger(choice) || choice < 0 || choice >= questions[questionId].options.length) return
    if (!quizTracking.current.started) {
      quizTracking.current.started = true
      analytics.track('quizStarted')
    }
    quizTracking.current.answered = true
    saveAnswers([...answers, { question: questionId, choice }])
  }
  const back = () => {
    if (submitting) return
    if (answers.length) saveAnswers(answers.slice(0, -1))
    else change({ ...payload, step: 0 })
  }
  const restart = () => {
    if (submitting) return
    quizTracking.current = { started: false, completed: false, answered: false }
    saveAnswers([])
  }
  const complete = () => {
    if (!introduced || !result || !content || submitting) return
    const submission = quizSubmission(answers, content)
    onComplete({
      ...payload,
      communicationStyle,
      displayName: name.trim(),
      tags: submission.tags,
      journey: submission.journey,
      custom: {
        variant: 'quiz', name: name.trim(), quiz_answers: answers,
        // Human-readable context keeps the existing backend independent of quiz IDs.
        quiz_context: answers.map(({ question, choice }) => ({ question: questions[question].title, answer: questions[question].options[choice] })),
        result: { stage: result.currentStage, stage_name: result.currentStageName, description: result.description },
      },
    })
  }
  return { ...identity, introduction, introduced, answers, questionId, result, content, screenRef, screenKey, start, choose, back, restart, complete }
}
